import { useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Star, FolderOpen, Image as ImageIcon } from "lucide-react";
import type { InfographicSectionType } from "../../../config/infographicTemplate";
import PortfolioPickerModal, { type PortfolioAsset } from "./PortfolioPickerModal";

/**
 * Editor for the infographic template's sections.
 *
 * One schema-driven editor rather than eighteen hand-written ones: each section
 * declares its fields here, so the builder, the renderer and the validator all
 * stay in step when a section changes.
 */

type FieldKind = "text" | "textarea" | "strings" | "rows" | "tiers" | "gallery";

interface Field {
  key: string;
  label: string;
  kind: FieldKind;
  placeholder?: string;
  hint?: string;
  /** For "rows": the sub-fields of each row. */
  columns?: { key: string; label: string; wide?: boolean }[];
  addLabel?: string;
}

type Columns = NonNullable<Field["columns"]>;

const ROW = (columns: Columns, key = "items", label = "Items", addLabel = "Add item"): Field => ({
  key,
  label,
  kind: "rows",
  columns,
  addLabel,
});

const SCHEMA: Partial<Record<InfographicSectionType, Field[]>> = {
  cover: [
    { key: "eyebrow", label: "Kicker", kind: "text", placeholder: "e.g. Logo design proposal", hint: "Optional pill above the headline." },
    { key: "headline", label: "Headline", kind: "text", placeholder: "The one line that frames the whole proposal" },
    { key: "subheadline", label: "Opening paragraph", kind: "textarea", placeholder: "Show you understood what they are really asking for" },
    { key: "ledes", label: "Further paragraphs", kind: "strings", addLabel: "Add paragraph", hint: "Keep the opening to two short paragraphs." },
    ROW(
      [{ key: "value", label: "In the ring" }, { key: "label", label: "Label" }, { key: "sub", label: "Sub-label" }],
      "badges",
      "Credential rings",
      "Add credential"
    ),
  ],
  "client-info": [
    { key: "company", label: "Company", kind: "text" },
    { key: "name", label: "Contact name", kind: "text" },
    { key: "email", label: "Email", kind: "text" },
    { key: "phone", label: "Phone", kind: "text" },
    { key: "website", label: "Website", kind: "text" },
    { key: "note", label: "Note", kind: "textarea" },
  ],
  "executive-summary": [
    { key: "text", label: "Summary", kind: "textarea", placeholder: "The short version for whoever signs off" },
    ROW([{ key: "value", label: "Value" }, { key: "label", label: "Label" }], "highlights", "Highlight metrics", "Add metric"),
  ],
  understanding: [
    { key: "text", label: "What we understood", kind: "textarea" },
    { key: "points", label: "Key requirements", kind: "strings", addLabel: "Add requirement" },
    { key: "callout", label: "Callout", kind: "textarea", hint: "One sentence that reframes the brief." },
  ],
  "proposed-solution": [
    { key: "text", label: "Solution overview", kind: "textarea" },
    ROW(
      [{ key: "title", label: "Title" }, { key: "description", label: "Description", wide: true }, { key: "icon", label: "Icon" }],
      "pillars",
      "Solution pillars",
      "Add pillar"
    ),
  ],
  "scope-of-work": [
    ROW([{ key: "title", label: "Work item" }, { key: "description", label: "Detail", wide: true }], "items", "In scope", "Add scope item"),
    { key: "excluded", label: "Not included", kind: "strings", addLabel: "Add exclusion" },
  ],
  services: [
    ROW(
      [{ key: "title", label: "Service" }, { key: "description", label: "What it covers", wide: true }, { key: "price", label: "Price" }, { key: "icon", label: "Icon" }],
      "items",
      "Services",
      "Add service"
    ),
  ],
  deliverables: [
    ROW(
      [{ key: "title", label: "Deliverable" }, { key: "description", label: "Detail", wide: true }, { key: "format", label: "Format" }],
      "items",
      "Deliverables",
      "Add deliverable"
    ),
  ],
  approach: [
    { key: "subtitle", label: "Subtitle", kind: "text" },
    ROW([{ key: "title", label: "Step" }, { key: "description", label: "What happens", wide: true }], "steps", "Steps", "Add step"),
  ],
  process: [
    ROW([{ key: "title", label: "Stage" }, { key: "description", label: "What happens", wide: true }], "steps", "Workflow", "Add stage"),
  ],
  timeline: [
    ROW(
      [{ key: "title", label: "Milestone" }, { key: "duration", label: "Duration" }, { key: "description", label: "Detail", wide: true }],
      "milestones",
      "Milestones",
      "Add milestone"
    ),
  ],
  pricing: [
    { key: "currency", label: "Currency", kind: "text", placeholder: "USD" },
    { key: "tiers", label: "Pricing options", kind: "tiers" },
    { key: "note", label: "Note under the pricing", kind: "textarea" },
  ],
  addons: [
    ROW([{ key: "title", label: "Add-on" }, { key: "description", label: "Detail", wide: true }, { key: "price", label: "Price" }], "items", "Add-ons", "Add add-on"),
  ],
  portfolio: [
    { key: "subtitle", label: "Card label", kind: "text", placeholder: "Recent work" },
    { key: "primaryTabLabel", label: "First tab label", kind: "text", placeholder: "For your industry" },
    { key: "projects", label: "Work for this client's industry", kind: "gallery" },
    { key: "note", label: "Note under the first tab", kind: "textarea" },
    { key: "bestTabLabel", label: "Second tab label", kind: "text", placeholder: "Best of my work" },
    { key: "bestProjects", label: "Best of your work", kind: "gallery", hint: "Leave empty to show a single list with no tabs." },
    { key: "bestNote", label: "Note under the second tab", kind: "textarea" },
  ],
  testimonials: [
    ROW(
      [
        { key: "quote", label: "Quote", wide: true },
        { key: "attribution", label: "Who said it" },
        { key: "rating", label: "Stars (1–5)" },
      ],
      "items",
      "Client reviews",
      "Add review"
    ),
  ],
  "why-us": [
    ROW([{ key: "value", label: "Value" }, { key: "label", label: "Label" }], "stats", "Proof metrics", "Add metric"),
    ROW([{ key: "title", label: "Reason" }, { key: "description", label: "Detail", wide: true }, { key: "icon", label: "Icon" }], "points", "Reasons", "Add reason"),
  ],
  terms: [
    { key: "text", label: "Introduction", kind: "textarea" },
    ROW([{ key: "title", label: "Term" }, { key: "text", label: "Detail", wide: true }], "items", "Terms", "Add term"),
  ],
  "next-steps": [
    { key: "text", label: "Closing message", kind: "textarea" },
    { key: "steps", label: "What happens next", kind: "strings", addLabel: "Add step" },
    { key: "buttonText", label: "Button label", kind: "text", placeholder: "Accept & Get Started" },
    { key: "buttonUrl", label: "Button link", kind: "text", hint: "Where the client goes when they accept." },
  ],
  contact: [
    { key: "name", label: "Name", kind: "text", hint: "Left empty, the freelance profile's own name is used." },
    { key: "email", label: "Email", kind: "text", hint: "Left empty, the brand's support email is used." },
    { key: "phone", label: "Phone", kind: "text" },
    { key: "website", label: "Website", kind: "text" },
    { key: "note", label: "Credentials line", kind: "textarea", placeholder: "Top Rated on Upwork, 100% Job Success, 90+ completed projects", hint: "Shown under your name in the hero and in the footer." },
  ],
};

export const INFOGRAPHIC_EDITOR_TYPES = Object.keys(SCHEMA) as InfographicSectionType[];

const input =
  "w-full rounded-xl border-2 border-gray-200 px-3.5 py-2.5 text-sm outline-none focus:border-emerald-500";

// ── list helpers ─────────────────────────────────────────────────────────────
const move = (arr: any[], from: number, to: number) => {
  if (to < 0 || to >= arr.length) return arr;
  const copy = arr.slice();
  const [x] = copy.splice(from, 1);
  copy.splice(to, 0, x);
  return copy;
};

const StringsEditor = ({ value, addLabel, onChange }: { value: any[]; addLabel?: string | undefined; onChange: (v: any[]) => void }) => {
  const rows = Array.isArray(value) ? value : [];
  return (
    <div className="space-y-2">
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            className={input}
            value={typeof row === "string" ? row : row?.title || ""}
            onChange={(e) => onChange(rows.map((r, j) => (j === i ? e.target.value : r)))}
          />
          <button onClick={() => onChange(rows.filter((_, j) => j !== i))} className="p-2 text-gray-400 hover:text-red-500">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        onClick={() => onChange([...rows, ""])}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-emerald-500 hover:text-emerald-600 text-sm font-semibold w-full justify-center"
      >
        <Plus className="w-4 h-4" /> {addLabel || "Add"}
      </button>
    </div>
  );
};

const RowsEditor = ({
  value,
  columns,
  addLabel,
  onChange,
}: {
  value: any[];
  columns: Columns;
  addLabel?: string | undefined;
  onChange: (v: any[]) => void;
}) => {
  const rows = Array.isArray(value) ? value : [];
  const set = (i: number, key: string, v: string) =>
    onChange(rows.map((r, j) => (j === i ? { ...(typeof r === "string" ? { title: r } : r), [key]: v } : r)));

  return (
    <div className="space-y-3">
      {rows.map((row, i) => {
        const r = typeof row === "string" ? { title: row } : row || {};
        return (
          <div key={i} className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">#{i + 1}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => onChange(move(rows, i, i - 1))} className="p-1 text-gray-400 hover:text-gray-700"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => onChange(move(rows, i, i + 1))} className="p-1 text-gray-400 hover:text-gray-700"><ChevronDown className="w-4 h-4" /></button>
                <button onClick={() => onChange(rows.filter((_, j) => j !== i))} className="p-1 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {columns.map((c) => (
                <div key={c.key} className={c.wide ? "sm:col-span-2" : ""}>
                  <label className="block text-[11px] font-semibold text-gray-500 mb-1">{c.label}</label>
                  {c.wide ? (
                    <textarea rows={2} className={input} value={r[c.key] || ""} onChange={(e) => set(i, c.key, e.target.value)} />
                  ) : (
                    <input className={input} value={r[c.key] || ""} onChange={(e) => set(i, c.key, e.target.value)} />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <button
        onClick={() => onChange([...rows, Object.fromEntries(columns.map((c) => [c.key, ""]))])}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-emerald-500 hover:text-emerald-600 text-sm font-semibold w-full justify-center"
      >
        <Plus className="w-4 h-4" /> {addLabel || "Add"}
      </button>
    </div>
  );
};

// Portfolio tiles: picked from the Portfolio Library so a client only ever
// sees real work, or added by hand when the sample lives elsewhere.
const GalleryEditor = ({ value, onChange }: { value: any[]; onChange: (v: any[]) => void }) => {
  const rows = Array.isArray(value) ? value : [];
  const [picking, setPicking] = useState(false);
  const set = (i: number, key: string, v: string) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)));

  return (
    <div className="space-y-3">
      {rows.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3">
          {rows.map((r, i) => (
            <div key={i} className="rounded-xl border border-gray-200 bg-gray-50/60 overflow-hidden">
              <div className="h-28 bg-white grid place-items-center border-b border-gray-200">
                {r.image ? (
                  <img src={r.image} alt={r.title || ""} className="h-full w-full object-contain p-2" />
                ) : (
                  <ImageIcon className="w-7 h-7 text-gray-300" />
                )}
              </div>
              <div className="p-2.5 space-y-2">
                <input className={input} placeholder="Project title" value={r.title || ""} onChange={(e) => set(i, "title", e.target.value)} />
                <input className={input} placeholder="★★★★★ · Logo + stationery" value={r.meta || ""} onChange={(e) => set(i, "meta", e.target.value)} />
                <input className={input} placeholder="Image URL" value={r.image || ""} onChange={(e) => set(i, "image", e.target.value)} />
                <div className="flex items-center justify-end gap-1">
                  <button onClick={() => onChange(move(rows, i, i - 1))} className="p-1 text-gray-400 hover:text-gray-700"><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => onChange(move(rows, i, i + 1))} className="p-1 text-gray-400 hover:text-gray-700"><ChevronDown className="w-4 h-4" /></button>
                  <button onClick={() => onChange(rows.filter((_, j) => j !== i))} className="p-1 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-2">
        <button
          onClick={() => setPicking(true)}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-emerald-300 text-emerald-600 hover:border-emerald-500 hover:bg-emerald-50 text-sm font-semibold"
        >
          <FolderOpen className="w-4 h-4" /> Add from Portfolio Library
        </button>
        <button
          onClick={() => onChange([...rows, { title: "", meta: "", image: "" }])}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-emerald-500 hover:text-emerald-600 text-sm font-semibold"
        >
          <Plus className="w-4 h-4" /> Add manually
        </button>
      </div>

      {picking && (
        <PortfolioPickerModal
          onClose={() => setPicking(false)}
          onPick={(assets: PortfolioAsset[]) =>
            onChange([
              ...rows,
              ...assets.map((a) => ({ title: a.name.replace(/\.[^/.]+$/, ""), meta: "", image: a.url })),
            ])
          }
        />
      )}
    </div>
  );
};

const TiersEditor = ({ value, onChange }: { value: any[]; onChange: (v: any[]) => void }) => {
  const tiers = Array.isArray(value) ? value : [];
  const set = (i: number, key: string, v: any) => onChange(tiers.map((t, j) => (j === i ? { ...t, [key]: v } : t)));

  return (
    <div className="space-y-3">
      {tiers.map((t, i) => (
        <div key={i} className={`rounded-xl border-2 p-3 ${t.featured ? "border-emerald-400 bg-emerald-50/50" : "border-gray-200 bg-gray-50/60"}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Option {i + 1}</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => onChange(tiers.map((x, j) => ({ ...x, featured: j === i ? !x.featured : false })))}
                title="Mark as recommended"
                className={`p-1 ${t.featured ? "text-emerald-600" : "text-gray-400 hover:text-emerald-600"}`}
              >
                <Star className="w-4 h-4" fill={t.featured ? "currentColor" : "none"} />
              </button>
              <button onClick={() => onChange(move(tiers, i, i - 1))} className="p-1 text-gray-400 hover:text-gray-700"><ChevronUp className="w-4 h-4" /></button>
              <button onClick={() => onChange(move(tiers, i, i + 1))} className="p-1 text-gray-400 hover:text-gray-700"><ChevronDown className="w-4 h-4" /></button>
              <button onClick={() => onChange(tiers.filter((_, j) => j !== i))} className="p-1 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 mb-1">Name</label>
              <input className={input} value={t.name || ""} onChange={(e) => set(i, "name", e.target.value)} />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 mb-1">Tag</label>
              <input className={input} placeholder="e.g. Most popular" value={t.tag || ""} onChange={(e) => set(i, "tag", e.target.value)} />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 mb-1">Price</label>
              <input className={input} placeholder="380" value={t.price || ""} onChange={(e) => set(i, "price", e.target.value)} />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 mb-1">Unit</label>
              <input className={input} placeholder="one-off / per month" value={t.unit || ""} onChange={(e) => set(i, "unit", e.target.value)} />
            </div>
          </div>

          <div className="mt-3">
            <label className="block text-[11px] font-semibold text-gray-500 mb-1">What's included</label>
            <StringsEditor value={t.features || []} addLabel="Add line" onChange={(v) => set(i, "features", v)} />
          </div>

          <div className="mt-3">
            <label className="block text-[11px] font-semibold text-gray-500 mb-1">Note</label>
            <input className={input} value={t.note || ""} onChange={(e) => set(i, "note", e.target.value)} />
          </div>
        </div>
      ))}

      <button
        onClick={() => onChange([...tiers, { name: "", tag: "", price: "", unit: "", features: [], note: "", featured: tiers.length === 0 }])}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-emerald-500 hover:text-emerald-600 text-sm font-semibold w-full justify-center"
      >
        <Plus className="w-4 h-4" /> Add pricing option
      </button>
    </div>
  );
};

// ── the editor ───────────────────────────────────────────────────────────────
interface Props {
  section: any;
  onUpdate: (updates: any) => void;
}

const InfographicSectionEditor = ({ section, onUpdate }: Props) => {
  const fields = SCHEMA[section.type as InfographicSectionType];
  if (!fields) return null;

  const content = section.content || {};
  const set = (key: string, value: any) => onUpdate({ content: { ...content, [key]: value } });

  return (
    <div className="space-y-5">
      {fields.map((f) => (
        <div key={f.key}>
          <label className="block text-sm font-semibold text-gray-700 mb-2">{f.label}</label>

          {f.kind === "text" && (
            <input className={input} placeholder={f.placeholder} value={content[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
          )}

          {f.kind === "textarea" && (
            <textarea rows={4} className={input} placeholder={f.placeholder} value={content[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
          )}

          {f.kind === "strings" && (
            <StringsEditor value={content[f.key] || []} addLabel={f.addLabel} onChange={(v) => set(f.key, v)} />
          )}

          {f.kind === "rows" && (
            <RowsEditor value={content[f.key] || []} columns={f.columns!} addLabel={f.addLabel} onChange={(v) => set(f.key, v)} />
          )}

          {f.kind === "tiers" && <TiersEditor value={content[f.key] || []} onChange={(v) => set(f.key, v)} />}

          {f.kind === "gallery" && <GalleryEditor value={content[f.key] || []} onChange={(v) => set(f.key, v)} />}

          {f.hint && <p className="mt-1.5 text-[11.5px] text-gray-400">{f.hint}</p>}
        </div>
      ))}
    </div>
  );
};

export default InfographicSectionEditor;
